import React, { useState, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { MoreVert } from "@mui/icons-material";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useQuery, gql } from "@apollo/client";
import { midgardClients } from "providers/MidgardProvider";
import { convertToCSV, downloadCSV } from "./configurationPageUtils";
import { ShiftSchedulesHighlightedText } from "./ShiftSchedulesHighlightedText";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";
import useWindowSize from "utils/customHooks/useWindowSize";

// Query to fetch sense configurations
export const SENSE_CONFIGS_QUERY = gql`
  query AllSenseConfigs {
    allSenseConfigs {
      serial
      processStateMachineConfig
      tapConfig
      role
      process {
        description
      }
    }
  }
`;

// Interface for processed sense config data
interface ProcessedSenseConfig {
  id: string;
  processName: string;
  serial: string;
  signalDriver: string;
  inputEvent?: string;
  downtimeThreshold?: string;
  idleMaxDuration?: number;
  idleMinDuration?: number;
}

// Interface for parsed processStateMachineConfig
interface ProcessStateMachineConfig {
  processConfig?: {
    activity?: {
      signalDriver?: string;
    };
    playbook: {
      fallbackActivityOptions?: ActivityOptions;
    };
  };
}

// Interface for sense config from API
interface SenseConfig {
  serial?: string;
  processStateMachineConfig?: string | ProcessStateMachineConfig;
  role?: string;
  process?: {
    description?: string;
  };
  tapConfig?: any; // Using any for now to access nested properties
}

// Format idle duration in a human-readable way
export const formatIdleDuration = (milliseconds?: number): string => {
  if (milliseconds === undefined) {
    return "--";
  }

  const totalSeconds = milliseconds / 1000;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    // Hours case
    let result = `${hours} ${hours === 1 ? "hour" : "hours"}`;
    if (minutes > 0) {
      result += ` ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }
    if (seconds > 0) {
      result += ` ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
    }
    return result;
  } else if (minutes > 0) {
    // Minutes case
    let result = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    if (seconds > 0) {
      result += ` ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
    }
    return result;
  } else {
    // Seconds only case
    return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  }
};

// Format downtime threshold based on min and max idle durations
export const formatDowntimeThreshold = (
  idleMinDuration?: number,
  idleMaxDuration?: number
): string => {
  // If both values are missing, return placeholder
  if (idleMinDuration === undefined && idleMaxDuration === undefined) {
    return "--";
  }

  // If only one value is defined, use that one
  if (idleMinDuration === undefined && idleMaxDuration !== undefined) {
    return formatIdleDuration(idleMaxDuration);
  }

  if (idleMaxDuration === undefined && idleMinDuration !== undefined) {
    return formatIdleDuration(idleMinDuration);
  }

  // If values are identical, show just one value
  if (idleMinDuration === idleMaxDuration) {
    return formatIdleDuration(idleMaxDuration);
  }

  // If values are different, show both
  return `min: ${formatIdleDuration(idleMinDuration)}, max: ${formatIdleDuration(idleMaxDuration)}`;
};

// SenseConfigsTable component for rendering the table
const SenseConfigsTable: React.FC<{
  configs: ProcessedSenseConfig[];
  searchTerm: string;
  sortBy: "processName" | "signalDriver" | "inputEvent" | "downtimeThreshold";
  sortDirection: "asc" | "desc";
  onSortChange: (
    column: "processName" | "signalDriver" | "inputEvent" | "downtimeThreshold"
  ) => void;
  page: number;
  rowsPerPage: number;
  loading: boolean;
}> = ({
  configs,
  searchTerm,
  sortBy,
  sortDirection,
  onSortChange,
  page,
  rowsPerPage,
  loading,
}) => {
  const { translate } = useFlightTranslation();
  // Calculate the current page's data when not loading
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPageData = configs.slice(startIndex, endIndex);

  // Empty state - only show when not loading and no data
  if (!loading && configs.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {searchTerm
          ? translate("No sense configs found matching your search.")
          : translate("No sense configs found.")}
      </Alert>
    );
  }

  // Helper to render a sortable column header
  const renderSortableHeader = (
    column: "processName" | "signalDriver" | "inputEvent" | "downtimeThreshold",
    label: string,
    width: string
  ) => {
    const isCurrentSort = sortBy === column;

    return (
      <th
        style={{
          padding: "12px",
          textAlign: "left",
          borderBottom: "1px solid #ddd",
          cursor: "pointer",
          verticalAlign: "middle",
          width,
          userSelect: "text",
        }}
        onClick={() => onSortChange(column)}
        onMouseOver={e => {
          e.currentTarget.style.backgroundColor = "#eeeeee";
        }}
        onMouseOut={e => {
          e.currentTarget.style.backgroundColor = "#f5f5f5";
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>{translate(label)}</span>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isCurrentSort &&
              (sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="small" />
              ) : (
                <ArrowUpwardIcon fontSize="small" />
              ))}
          </Box>
        </Box>
      </th>
    );
  };

  // Helper to render a cell with search term highlighting
  const renderCell = (content: string, highlight: string) => {
    const isCentered = content === "--";

    return (
      <td
        style={{
          padding: "8px 12px", // Reduced vertical padding
          verticalAlign: "middle",
          userSelect: "text",
          textAlign: isCentered ? "center" : "left",
          height: "40px", // Explicitly set the height
        }}
      >
        <ShiftSchedulesHighlightedText text={content} highlight={highlight} />
      </td>
    );
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          userSelect: "text",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            {renderSortableHeader("processName", "Process", "120px")}
            {renderSortableHeader("signalDriver", "Signal Driver *", "150px")}
            {renderSortableHeader("inputEvent", "Input Event", "150px")}
            {renderSortableHeader(
              "downtimeThreshold",
              "Downtime Threshold",
              "150px"
            )}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} style={{ textAlign: "center", padding: "40px" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>
                  {translate("Loading sense configs...")}
                </Typography>
              </td>
            </tr>
          ) : (
            currentPageData.map((config, index) => (
              <tr
                key={config.id}
                style={{
                  backgroundColor: index % 2 === 0 ? "white" : "#f9f9f9",
                  cursor: "pointer",
                  height: "40px",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(63, 81, 181, 0.08)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor =
                    index % 2 === 0 ? "white" : "#f9f9f9";
                }}
              >
                <td
                  style={{
                    borderBottom: "1px solid #ddd",
                    textAlign: "left",
                    verticalAlign: "middle",
                    userSelect: "text",
                    height: "40px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {renderCell(config.processName, searchTerm)}
                </td>
                <td
                  style={{
                    borderBottom: "1px solid #ddd",
                    textAlign: "center",
                    verticalAlign: "middle",
                    userSelect: "text",
                    height: "40px",
                  }}
                >
                  {renderCell(config.signalDriver, searchTerm)}
                </td>
                <td
                  style={{
                    borderBottom: "1px solid #ddd",
                    textAlign: "center",
                    verticalAlign: "middle",
                    userSelect: "text",
                    height: "40px",
                  }}
                >
                  {renderCell(config.inputEvent || "--", searchTerm)}
                </td>
                <td
                  style={{
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    userSelect: "text",
                    textAlign: "center",
                    height: "40px",
                  }}
                >
                  {formatDowntimeThreshold(
                    config.idleMinDuration,
                    config.idleMaxDuration
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Box>
  );
};

/**
 * Sense Configs Configuration Section
 */
export const SenseConfigsSection: React.FC = () => {
  const { translate } = useFlightTranslation();
  const cKey = useCustomerKey();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { innerHeight } = useWindowSize();

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Filter state for MAIN instances
  const [showOnlyMainInstances, setShowOnlyMainInstances] =
    useState<boolean>(true);

  // Search and sort states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<
    "processName" | "signalDriver" | "inputEvent" | "downtimeThreshold"
  >("processName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setPage(0); // Reset to first page when searching
    },
    []
  );

  // Clear search field
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setPage(0); // Reset to first page when clearing search
  }, []);

  // Handle opening the menu
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  // Handle closing the menu
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  // Handle toggle for showing only MAIN instances
  const handleToggleMainInstances = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowOnlyMainInstances(event.target.checked);
      setPage(0); // Reset to first page when toggling filter
    },
    []
  );

  // Handle column sort change
  const handleSortChange = useCallback(
    (
      column:
        | "processName"
        | "signalDriver"
        | "inputEvent"
        | "downtimeThreshold"
    ) => {
      if (sortBy === column) {
        // If already sorting by this column, toggle direction
        setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
      } else {
        // If sorting by a new column, set it and default to ascending
        setSortBy(column);
        setSortDirection("asc");
      }
    },
    [sortBy]
  );

  // Handle pagination
  const handleChangePage = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  // Query to get sense configurations
  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery(SENSE_CONFIGS_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "network-only",
    onError: err => {
      console.error("Error fetching sense configs:", err);
      setError("Failed to load sense configs. Please try again later.");
      setLoading(false);
    },
    onCompleted: () => {
      setLoading(false);
    },
  });

  // Process sense config data
  const processedConfigs = useMemo(() => {
    if (!data?.allSenseConfigs || !Array.isArray(data.allSenseConfigs)) {
      return [];
    }

    // Filter based on showOnlyMainInstances state
    return data.allSenseConfigs
      .filter((config: SenseConfig) => {
        // If not filtering for MAIN instances, include all non-MIRROR configs
        if (!showOnlyMainInstances) {
          return true;
        }

        // Only include configs that:
        // 1. Have role === "MAIN", or
        // 2. Have serial that ends with "_MAIN"
        const isMainRole = config.role?.toLowerCase() === "main";
        const hasMainSuffix = config.serial?.endsWith("_MAIN");

        return isMainRole || hasMainSuffix;
      })
      .map((config: SenseConfig, index) => {
        let psmProcessConfig:
          | ProcessStateMachineConfig["processConfig"]
          | undefined = undefined;

        // Parse the processStateMachineConfig JSON string
        try {
          const parsedPSMConfig: ProcessStateMachineConfig =
            typeof config.processStateMachineConfig === "string"
              ? JSON.parse(config.processStateMachineConfig)
              : (config.processStateMachineConfig as ProcessStateMachineConfig) ||
                {};

          psmProcessConfig = parsedPSMConfig?.processConfig;
        } catch (e) {
          console.error("Error parsing processStateMachineConfig:", e);
        }

        // Parse the tapConfig JSON string
        let inputEvent: string | undefined;
        try {
          if (config.tapConfig) {
            // If tapConfig is already parsed as an object
            if (typeof config.tapConfig === "object") {
              // Access the nested messageRouting.data.event path
              inputEvent = config.tapConfig.messageRouting?.data?.event;
            } else if (typeof config.tapConfig === "string") {
              // Parse the JSON string if it's a string
              const parsedTapConfig = JSON.parse(config.tapConfig);
              inputEvent = parsedTapConfig?.messageRouting?.data?.event;
            }
          }
        } catch (e) {
          console.error("Error parsing tapConfig:", e);
        }

        const activityOptions = getActivityOptions(
          psmProcessConfig?.playbook?.fallbackActivityOptions,
          psmProcessConfig?.activity?.signalDriver as ActivitySignalDriverType
        );

        return {
          id: `sense-config-${index}`,
          processName:
            config.process?.description ||
            `Sense Config ${config.serial || index + 1}`,
          serial: config.serial || "",
          signalDriver: psmProcessConfig?.activity?.signalDriver || "",
          inputEvent,
          downtimeThreshold: formatDowntimeThreshold(
            activityOptions.idleMinDuration,
            activityOptions.idleMaxDuration
          ),
          idleMaxDuration: activityOptions.idleMaxDuration,
          idleMinDuration: activityOptions.idleMinDuration,
        };
      });
  }, [data?.allSenseConfigs, showOnlyMainInstances]);

  // Filter and sort configs based on search term and sort direction
  const filteredAndSortedConfigs = useMemo(() => {
    if (!processedConfigs || processedConfigs.length === 0) {
      return [];
    }

    let result = [...processedConfigs];

    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      result = result.filter(config => {
        return (
          config.processName.toLowerCase().includes(searchTermLower) ||
          config.signalDriver?.toLowerCase().includes(searchTermLower) ||
          config.inputEvent?.toLowerCase().includes(searchTermLower) ||
          config.downtimeThreshold?.toLowerCase().includes(searchTermLower)
        );
      });
    }

    // Apply sorting based on selected column
    result.sort((a, b) => {
      // For text columns
      if (sortBy === "signalDriver") {
        const valueA = (a.signalDriver || "").toLowerCase();
        const valueB = (b.signalDriver || "").toLowerCase();
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (sortBy === "processName") {
        const valueA = a.processName.toLowerCase();
        const valueB = b.processName.toLowerCase();
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (sortBy === "inputEvent") {
        const valueA = (a.inputEvent || "").toLowerCase();
        const valueB = (b.inputEvent || "").toLowerCase();
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      // For numeric columns
      else if (sortBy === "downtimeThreshold") {
        const valueA = a.idleMaxDuration || 0;
        const valueB = b.idleMaxDuration || 0;
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      }

      // Default fallback
      return 0;
    });

    return result;
  }, [processedConfigs, searchTerm, sortBy, sortDirection]);

  // Export data to CSV
  const handleExportCsv = useCallback(() => {
    if (!filteredAndSortedConfigs || filteredAndSortedConfigs.length === 0) {
      return;
    }

    const csvData = filteredAndSortedConfigs.map(config => {
      // Ensure values exist or provide defaults to prevent errors
      return {
        Process: config.processName || "Unnamed Process",
        "Serial Number": config.serial || "Not specified",
        "Signal Driver": config.signalDriver || "Not specified",
        "Input Event": config.inputEvent || "Not specified",
        "Downtime Threshold": config.downtimeThreshold,
      };
    });

    const csvContent = convertToCSV(csvData);
    downloadCSV(
      csvContent,
      `sense-configs-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }, [filteredAndSortedConfigs]);

  if (loading || queryLoading) {
    // Now we'll use the main return structure but pass loading=true to the table component
    return (
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <div>
            <Typography variant="h5">{translate("Sense Configs")}</Typography>
          </div>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title={translate("Table Settings")}>
              <IconButton onClick={handleMenuOpen} color="primary">
                <MoreVert />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Menu for Settings and Export */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 220, maxWidth: 280 },
          }}
          disableScrollLock={true}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          {/* Show MAIN instances toggle */}
          <MenuItem
            sx={{
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyMainInstances}
                  onChange={handleToggleMainInstances}
                  size="small"
                />
              }
              label={translate("Show only MAIN instances")}
            />
          </MenuItem>

          {/* Download CSV */}
          <MenuItem
            onClick={() => {
              handleExportCsv();
              handleMenuClose();
            }}
            disabled={
              !filteredAndSortedConfigs || filteredAndSortedConfigs.length === 0
            }
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FileDownloadIcon sx={{ mr: 1, fontSize: "1.25rem" }} />
              {translate("Download CSV")}
            </Box>
          </MenuItem>
        </Menu>

        {/* Search Field */}
        <Box
          sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}
        >
          <TextField
            fullWidth
            placeholder={translate("Search sense configs...")}
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={true} // Disabled during loading
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    size="small"
                    disabled={true}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Table with Loading State */}
        <SenseConfigsTable
          configs={[]} // Empty array during loading
          searchTerm={searchTerm}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          page={page}
          rowsPerPage={rowsPerPage}
          loading={true} // Pass loading state to table
        />

        {/* Empty Pagination for structure */}
        <TablePagination
          component="div"
          count={0}
          page={0}
          onPageChange={() => {}}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[5, 10, 25, 50]}
          disabled={true}
        />
      </Paper>
    );
  }

  if (error || queryError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">{translate("Sense Configs")}</Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error || queryError?.message || "An unknown error occurred"}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {translate("Retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <div>
          <Typography variant="h5">{translate("Sense Configs")}</Typography>
        </div>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title={translate("Table Settings")}>
            <IconButton onClick={handleMenuOpen} color="primary">
              <MoreVert />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Menu for Settings and Export */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 220, maxWidth: 280 },
        }}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {/* Show MAIN instances toggle */}
        <MenuItem
          sx={{
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyMainInstances}
                onChange={handleToggleMainInstances}
                size="small"
              />
            }
            label={translate("Show only MAIN instances")}
          />
        </MenuItem>

        {/* Export to CSV */}
        <MenuItem
          onClick={() => {
            handleExportCsv();
            handleMenuClose();
          }}
          disabled={
            !filteredAndSortedConfigs || filteredAndSortedConfigs.length === 0
          }
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FileDownloadIcon sx={{ mr: 1, fontSize: "1.25rem" }} />
            {translate("Export to CSV")}
          </Box>
        </MenuItem>
      </Menu>

      {/* Search Field */}
      <Box sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}>
        <TextField
          fullWidth
          placeholder={translate("Search sense configs...")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Table Content */}
      <SenseConfigsTable
        configs={filteredAndSortedConfigs}
        searchTerm={searchTerm}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading || queryLoading}
      />

      {/* Pagination */}
      {filteredAndSortedConfigs.length > 0 && (
        <TablePagination
          component="div"
          count={filteredAndSortedConfigs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mb: 1 }}
        />
      )}

      {/* Signal Driver explainer */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          mx: 2,
          p: 2,
          backgroundColor: "#f9f9f9",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          color: "grey",
          userSelect: "all",
        }}
      >
        <Typography variant="body2" component="div">
          <strong style={{ color: "black" }}>* Signal Driver</strong>:
          Determines how a process transitions between active and idle states.
          <ul
            style={{ marginTop: "8px", paddingLeft: "20px", lineHeight: "1.5" }}
          >
            <li>
              <strong style={{ color: "black" }}>
                grossCount/netCount/rejectCount
              </strong>
              : Determines active/idle state from respective count signals.
              Downtime Threshold is configurable.
            </li>
            <li>
              <strong style={{ color: "black" }}>direct</strong>: Directly
              follows the activity signal without delay. Downtime Threshold does
              not apply.
            </li>
            <li>
              <strong style={{ color: "black" }}>directThreshold</strong>:
              Similar to direct, but with a configurable Downtime Threshold
              window. The timer starts on first idle signal, then process
              becomes idle if no active signal received in that window.
            </li>
          </ul>
        </Typography>
      </Box>

      {/* JSON Editor (visible only in development mode) */}
      {process.env.NODE_ENV === "development" && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            * JSON viewer below is only visible in development mode
          </Typography>
          <CustomJsonEditor
            key={
              JSON.stringify(filteredAndSortedConfigs) +
              JSON.stringify(data?.allSenseConfigs)
            }
            value={{
              senseConfigs: data?.allSenseConfigs || [],
              processedSenseConfigs: processedConfigs,
            }}
            height={(innerHeight - 350) / 2}
          />
        </Box>
      )}
    </Paper>
  );
};

// * copied from libs/process-state-machine/src/hooks/hooks.ts
// Apply activity machine options. Read the priority last to first, that means
// the order is: settings from the play, then from the fallback config, then
// from the defaults.
const getActivityOptions = (
  fallbackActivityOptions?: ActivityOptions,
  // play?: undefined | null,
  signalDriver?: ActivitySignalDriverType
): ActivityOptions => {
  const playbookOptions = fallbackActivityOptions;

  // IntervalMultiplier and tick duration is not applicable to direct or directthreshold signaldrivers.
  // to set max wait time to idle into threshold, min and max is set threshold value.
  if (
    signalDriver === ActivitySignalDriverType.DIRECT_THRESHOLD &&
    playbookOptions?.threshold
  ) {
    const threshold = playbookOptions?.threshold;

    return {
      idleIntervalMultiplier: 1,
      tickDuration: 0,
      idleMinDuration: threshold,
      idleMaxDuration: threshold,
    };
  }
  // const playOptions = play?.activityOptions;
  return {
    ...defaultActivityOptions,
    ...playbookOptions,
    // ...playOptions,
  };
};

interface ActivityOptions {
  maxCountsToKeep?: number;
  /**
   * Multiplier for wait until counts arrive, e.g if 1.5, waits 1.5 times of
   * time interval between last two counts
   */
  idleIntervalMultiplier: number;
  /**
   * The minimum duration to wait until transition from active to idle
   */
  idleMinDuration: number;
  /**
   * The maximum duration to wait until transition from active to idle
   */
  idleMaxDuration: number;
  /**
   * A constant duration to append to the timestamp of each incoming count sample.
   *
   * Note: this helps especially in scenarios where a single count causes an ACTIVE
   * state transition, but no other counts come in. If not for this field, there would
   * be a zero-duration producing segment recorded and visible in Flight as a segment.
   * But with this field, we'd at least see a producing segment with a duration equal
   * to this value (5 seconds by default).
   */
  tickDuration: number;
  /**
   *  A constant duration used to set a threshold for idle segment.
   *
   * Note: A segment is considered idle only if the segment duration more then the threshold.
   * The default value is 0s. This parameter applies only for direct signal.
   * When a  low signal arrives, system waits for the threshold to expire before entering idle state.
   * If a high signal arrives before the threshold is reached, the system continues to be in producing state
   */
  threshold?: number;
}
/** The signal the activity machine listens for to enter its ACTIVE state. */
export enum ActivitySignalDriverType {
  GROSS_COUNT = "grossCount",
  NET_COUNT = "netCount",
  REJECT_COUNT = "rejectCount",
  DIRECT = "direct",
  DIRECT_THRESHOLD = "directThreshold",
}
const defaultActivityOptions: ActivityOptions = {
  idleMinDuration: 60000,
  idleMaxDuration: 60000,
  idleIntervalMultiplier: 1,
  tickDuration: 5000,
};
